import cn from "classnames";
import { t } from "i18next";
import { Button } from "ui";

import { ConversationContact } from "../../aiConversations.types";

import styles from "./ChatHeader.module.scss";

type ToggleMode = "ai" | "human";

interface Props {
  contact: ConversationContact;
  mode: ToggleMode;
  onModeChange: (next: ToggleMode) => void;
  submitting?: boolean;
  disabled?: boolean;
  onCreateReturn?: () => void;
}

const SparkleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M10 1L12 7.5L18.5 9.5L12 11.5L10 18L8 11.5L1.5 9.5L8 7.5L10 1Z"
      fill="url(#sparkleGrad)"
    />
    <defs>
      <linearGradient id="sparkleGrad" x1="1.5" y1="1" x2="18.5" y2="18" gradientUnits="userSpaceOnUse">
        <stop stopColor="#FB3973" />
        <stop offset="1" stopColor="#9933FF" />
      </linearGradient>
    </defs>
  </svg>
);

const ContactAvatar = ({ name, url }: { name: string; url?: string }) => {
  if (url) {
    return <img className={styles.avatarImg} src={url} alt={name} />;
  }
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return <div className={styles.avatar}>{initials}</div>;
};

const CompactSwitch = ({
  active,
  onChange,
  disabled
}: {
  active: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    disabled={disabled}
    className={cn(styles.compactSwitch, { [styles.compactSwitchActive]: active })}
    onClick={() => onChange(!active)}
  >
    <span className={styles.compactSwitchKnob} />
  </button>
);

const ChatHeader = ({
  contact,
  mode,
  onModeChange,
  submitting,
  disabled,
  onCreateReturn
}: Props) => {
  const aiActive = mode === "ai";

  const handleToggle = (nextActive: boolean) => {
    onModeChange(nextActive ? "ai" : "human");
  };

  return (
    <div className={styles.header}>
      <div className={styles.contact}>
        <ContactAvatar name={contact.name} url={contact.avatarUrl} />
        <div className={styles.contactInfo}>
          <div className={styles.contactName}>{contact.name}</div>
          {contact.phone && <div className={styles.contactPhone}>{contact.phone}</div>}
        </div>
      </div>

      {/* An agent can start a return from the conversation itself — the
          assistant may be down, or may not have read the complaint as one. */}
      {onCreateReturn && (
        <Button
          size="sm"
          title={t("Create return")}
          className={styles.returnButton}
          onClick={onCreateReturn}
        />
      )}

      <div className={styles.aiToggle}>
        <SparkleIcon />
        <span className={styles.aiToggleLabel}>AI for this conversation</span>
        <CompactSwitch
          active={aiActive}
          onChange={handleToggle}
          disabled={submitting || disabled}
        />
        <span className={styles.aiToggleState}>{aiActive ? "ON" : "OFF"}</span>
      </div>
    </div>
  );
};

export default ChatHeader;
